/**
 * All available locale definitions for the application
 * Maps locale codes to their full configuration
 * - code: locale code for i18n
 * - iso: ISO locale format
 * - file: translation file name
 * - name: display name in the locale's language
 * - countryCode: ISO 3166-1 alpha-2 country code for flag icons
 */
export const allLocaleDefinitions = {
  "hu-HU": {
    code: "hu-HU",
    iso: "hu-HU",
    file: "hu-HU.json",
    name: "Magyar",
    countryCode: "hu",
  },
  "cs-CZ": {
    code: "cs-CZ",
    iso: "cs-CZ",
    file: "cs-CZ.json",
    name: "Čeština",
    countryCode: "cz",
  },
  "de-DE": {
    code: "de-DE",
    iso: "de-DE",
    file: "de-DE.json",
    name: "Deutsch",
    countryCode: "de",
  },
  "en-US": {
    code: "en-US",
    iso: "en-US",
    file: "en-US.json",
    name: "English",
    countryCode: "us",
  },
  "hr-HR": {
    code: "hr-HR",
    iso: "hr-HR",
    file: "hr-HR.json",
    name: "Hrvatski",
    countryCode: "hr",
  },
  "pl-PL": {
    code: "pl-PL",
    iso: "pl-PL",
    file: "pl-PL.json",
    name: "Polski",
    countryCode: "pl",
  },
};

/**
 * Get the list of locale configurations for a specific brand
 * @param {Object} brandConfig - Brand configuration object containing supportedLocales
 * @returns {Array<Object>} Array of locale definitions for the brand
 *
 * If the brand has supportedLocales defined, returns those locales.
 * Otherwise, returns Hungarian as the default locale.
 */
export function getLocalesForBrand(brandConfig) {
  // If brand has supportedLocales, use that
  if (brandConfig.supportedLocales && brandConfig.supportedLocales.length > 0) {
    return brandConfig.supportedLocales
      .map((code) => allLocaleDefinitions[code])
      .filter(Boolean);
  }

  // Fallback: return only Hungarian if no supportedLocales defined
  return [allLocaleDefinitions["hu-HU"]];
}

/**
 * Get the country code for a locale (used for flag icons)
 * @param {string} localeCode - Locale code (e.g., 'hu-HU', 'en-US', 'cs-CZ', 'de-DE')
 * @returns {string} Country code for the locale, or the locale code itself if not found
 */
export function getCountryCode(localeCode) {
  return allLocaleDefinitions[localeCode]?.countryCode || localeCode;
}

/**
 * Get the display name for a locale
 * @param {string} localeCode - Locale code (e.g., 'hu-HU', 'en-US', 'cs-CZ', 'de-DE')
 * @returns {string} Display name for the locale, or the locale code itself if not found
 */
export function getLocaleName(localeCode) {
  return allLocaleDefinitions[localeCode]?.name || localeCode;
}
